import * as React from 'react';
import { useState, useRef } from 'react';
import {
    useTranslate,
    useDataProvider,
    useNotify,
    TextInput,
    NumberInput,
    BooleanInput,
    required,
    minValue,
    maxValue,
    minLength,
} from 'react-admin';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Link,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Divider,
    Alert,
    CircularProgress,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SchoolIcon from '@mui/icons-material/School';
import Face5Icon from '@mui/icons-material/Face5';
import FaceIcon from '@mui/icons-material/Face';
import DomainIcon from '@mui/icons-material/Domain';
import Diversity1Icon from '@mui/icons-material/Diversity1';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import SettingsIcon from '@mui/icons-material/Settings';
import SaveIcon from '@mui/icons-material/Save';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { WizardForm, WizardFormStep } from '@react-admin/ra-form-layout';

interface TestScenario {
    name: string;
    description?: string;
    school_guid?: string;
    school_name?: string;
    groups?: { guid: string; name: string }[];
    teacher_count: number;
    pupil_count: number;
    student_count: number;
    created: string;
}

interface TestScenarioResult {
    name: string;
    group_guid: string;
    teachers: { username: string; firstname: string; lastname: string }[];
    pupils: { firstname: string; lastname: string; keycode: string }[];
    students: { username: string; firstname: string; lastname: string }[];
}

interface School {
    id: string;
    guid: string;
    name: string;
}

interface Product {
    id: string;
    guid: string;
    name: string;
    isbn?: string;
    target_persons?: string[];
    kouluasteet?: string[];
    applications?: string[];
}

const PUPIL_APP_GUID = '5b846602-4428-4c2d-b12a-d2b56dd722b7';
const STUDENT_APP_GUID = 'a7faf0f2-3cfc-4f62-9a77-aa75134ac5a4';

const getProductRoleLabels = (applications?: string[]): string[] => {
    const labels: string[] = [];
    if (applications?.includes(PUPIL_APP_GUID)) labels.push('oppilas');
    if (applications?.includes(STUDENT_APP_GUID)) labels.push('opiskelija');
    return labels;
};

const TestScenarioPanel = () => {
    const translate = useTranslate();
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const queryClient = useQueryClient();
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [resultDialogOpen, setResultDialogOpen] = useState(false);
    const [detailScenario, setDetailScenario] = useState<TestScenario | null>(
        null
    );
    const [lastResult, setLastResult] = useState<TestScenarioResult | null>(
        null
    );
    const [schoolSearch, setSchoolSearch] = useState('');
    const [selectedSchool, setSelectedSchool] = useState<School | null>(null);
    const [productSearch, setProductSearch] = useState('');
    const [selectedProducts, setSelectedProducts] = useState<Product[]>([]);
    const persistAfterCreateRef = useRef(false);

    // Fetch existing scenarios
    const { data: scenariosData, isLoading } = useQuery({
        queryKey: ['test-scenarios'],
        queryFn: () => dataProvider.getTestScenarios(),
    });

    const scenarios: TestScenario[] = scenariosData?.data || [];

    // Fetch persisted scenario names from S3
    const { data: persistedData } = useQuery({
        queryKey: ['test-scenarios-persisted'],
        queryFn: () => dataProvider.getPersistedTestScenarios(),
    });

    const persistedNames: Set<string> = new Set(persistedData?.data || []);

    // Search schools
    const { data: schoolsData } = useQuery({
        queryKey: ['schools-search', schoolSearch],
        queryFn: () =>
            dataProvider.getList('school', {
                pagination: { page: 1, perPage: 20 },
                sort: { field: 'name', order: 'ASC' },
                filter: { q: schoolSearch },
            }),
        enabled: schoolSearch.length >= 2,
    });

    const schools: School[] = (schoolsData?.data || []).map((s: any) => ({
        id: s.id || s.guid,
        guid: s.guid || s.id,
        name: s.name,
    }));

    // Search products with test scenario filters
    const { data: productsData } = useQuery({
        queryKey: ['products-search', productSearch],
        queryFn: () =>
            dataProvider.getList('product', {
                pagination: { page: 1, perPage: 50 },
                sort: { field: 'name', order: 'ASC' },
                filter: {
                    q: productSearch,
                    status: 0,
                    product_type: '1022060516',
                    applications: `${PUPIL_APP_GUID},${STUDENT_APP_GUID}`,
                },
            }),
        enabled: productSearch.length >= 2,
    });

    const products: Product[] = (productsData?.data || [])
        .filter((p: any) => {
            const pub = p.public || {};
            if (!pub.category) return false;
            if (
                pub.free_to_download === 'true' ||
                pub.free_to_download === true
            )
                return false;
            const hasStudent =
                Array.isArray(pub.target_persons) &&
                pub.target_persons.includes('student');
            const isSumplattava =
                pub.sumplattava === 'true' || pub.sumplattava === true;
            if (!hasStudent && !isSumplattava) return false;
            return true;
        })
        .map((p: any) => ({
            id: p.id || p.guid,
            guid: p.guid || p.id,
            name: p.name,
            isbn: p.public?.materialstorage?.isbn || '',
            target_persons: p.public?.target_persons || [],
            kouluasteet: p.public?.kouluasteet || [],
            applications: p.public?.applications || [],
        }));

    // Fetch group detail for selected scenario (first group)
    const detailFirstGroupGuid = detailScenario?.groups?.[0]?.guid;
    const { data: detailGroupData } = useQuery({
        queryKey: ['group-detail', detailFirstGroupGuid],
        queryFn: () =>
            dataProvider.getOne('group', { id: detailFirstGroupGuid }),
        enabled: !!detailFirstGroupGuid,
    });

    const detailGroup = detailGroupData?.data;
    const detailInfo = detailGroup?.info?.public || {};

    // Delete mutation
    const deleteMutation = useMutation({
        mutationFn: (name: string) => dataProvider.deleteTestScenario(name),
        onSuccess: () => {
            notify(translate('hallinta.tests.delete_success'), {
                type: 'success',
            });
            queryClient.invalidateQueries({ queryKey: ['test-scenarios'] });
        },
        onError: (error: any) => {
            notify(error?.message || 'Error', { type: 'error' });
        },
    });

    // Create mutation
    const createMutation = useMutation({
        mutationFn: (data: any) => dataProvider.createTestScenario(data),
        onSuccess: (response: any) => {
            notify(translate('hallinta.tests.create_success'), {
                type: 'success',
            });
            queryClient.invalidateQueries({ queryKey: ['test-scenarios'] });
            setCreateDialogOpen(false);
            setLastResult(response.data);
            setResultDialogOpen(true);
            setSelectedSchool(null);
            setSelectedProducts([]);
            // Auto-persist to S3 if requested
            if (persistAfterCreateRef.current && response.data?.name) {
                persistMutation.mutate(response.data.name);
            }
            persistAfterCreateRef.current = false;
        },
        onError: (error: any) => {
            notify(error?.body?.message || error?.message || 'Error', {
                type: 'error',
            });
        },
    });

    // Persist mutation (save to S3)
    const persistMutation = useMutation({
        mutationFn: (name: string) => dataProvider.persistTestScenario(name),
        onSuccess: () => {
            notify(translate('hallinta.tests.persist_success'), {
                type: 'success',
            });
            queryClient.invalidateQueries({
                queryKey: ['test-scenarios-persisted'],
            });
        },
        onError: (error: any) => {
            notify(error?.body?.message || error?.message || 'Error', {
                type: 'error',
            });
        },
    });

    // Unpersist mutation (remove from S3)
    const unpersistMutation = useMutation({
        mutationFn: (name: string) => dataProvider.unpersistTestScenario(name),
        onSuccess: () => {
            notify(translate('hallinta.tests.unpersist_success'), {
                type: 'success',
            });
            queryClient.invalidateQueries({
                queryKey: ['test-scenarios-persisted'],
            });
        },
        onError: (error: any) => {
            notify(error?.body?.message || error?.message || 'Error', {
                type: 'error',
            });
        },
    });

    const handleDelete = (name: string) => {
        if (
            window.confirm(translate('hallinta.tests.delete_confirm', { name }))
        ) {
            deleteMutation.mutate(name);
        }
    };

    const handleCreate = (formData: any) => {
        if (!selectedSchool) {
            notify('Valitse koulu', { type: 'error' });
            return;
        }

        const payload = {
            name: formData.name,
            description: formData.description || '',
            school_guid: selectedSchool.guid,
            product_guids: selectedProducts.map(p => p.guid),
            class_name: formData.class_name || formData.name,
            class_code: formData.class_code || '',
            class_highschool: formData.class_highschool || false,
            add_learners_to_groups: formData.add_learners_to_groups ?? true,
            assign_licenses: formData.assign_licenses ?? true,
            accept_terms: formData.accept_terms ?? true,
            skip_remember_me: formData.skip_remember_me ?? true,
            class_size: formData.class_size || 0,
            teachers: {
                count: formData.teacher_count || 0,
                firstname_prefix: formData.teacher_firstname_prefix || 'Testi',
                lastname: formData.teacher_lastname || 'Opettaja',
                email_domain: formData.teacher_email_domain || 'testi.fix',
                password: formData.teacher_password || 'test1=pe',
                mpass: formData.teacher_mpass ?? false,
            },
            pupils: {
                count: formData.pupil_count || 0,
                firstname_prefix: formData.pupil_firstname_prefix || 'Testi',
                lastname: formData.pupil_lastname || 'Oppilas',
                keycode_prefix: formData.pupil_keycode_prefix || 'niitty',
                mpass: formData.pupil_mpass ?? false,
            },
            students: {
                count: formData.student_count || 0,
                firstname_prefix: formData.student_firstname_prefix || 'Testi',
                lastname: formData.student_lastname || 'Opiskelija',
                email_domain: formData.student_email_domain || 'testi.fix',
                password: formData.student_password || 'test1=pe',
                mpass: formData.student_mpass ?? false,
            },
        };

        persistAfterCreateRef.current = !!formData.persist_to_s3;
        createMutation.mutate(payload);
    };

    return (
        <Box>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                }}
            >
                <Typography variant="h6">
                    {translate('hallinta.tests.title')}
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => {
                        setSelectedSchool(null);
                        setSelectedProducts([]);
                        setSchoolSearch('');
                        setProductSearch('');
                        setCreateDialogOpen(true);
                    }}
                >
                    {translate('hallinta.tests.create')}
                </Button>
            </Box>

            {/* Scenarios list */}
            {isLoading ? (
                <CircularProgress />
            ) : scenarios.length === 0 ? (
                <Alert severity="info">
                    {translate('hallinta.tests.no_scenarios')}
                </Alert>
            ) : (
                <TableContainer component={Paper}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    {translate('hallinta.tests.fields.name')}
                                </TableCell>
                                <TableCell>
                                    {translate(
                                        'hallinta.tests.fields.school_name'
                                    )}
                                </TableCell>
                                <TableCell>
                                    {translate(
                                        'hallinta.tests.fields.group_name'
                                    )}
                                </TableCell>
                                <TableCell align="center">
                                    {translate(
                                        'hallinta.tests.fields.teacher_count'
                                    )}
                                </TableCell>
                                <TableCell align="center">
                                    {translate(
                                        'hallinta.tests.fields.pupil_count'
                                    )}
                                </TableCell>
                                <TableCell align="center">
                                    {translate(
                                        'hallinta.tests.fields.student_count'
                                    )}
                                </TableCell>
                                <TableCell>
                                    {translate('hallinta.tests.fields.created')}
                                </TableCell>
                                <TableCell />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {scenarios.map(scenario => (
                                <TableRow key={scenario.name}>
                                    <TableCell>
                                        <Link
                                            component="button"
                                            underline="hover"
                                            onClick={() =>
                                                setDetailScenario(scenario)
                                            }
                                            sx={{ fontWeight: 'bold' }}
                                        >
                                            {scenario.name}
                                        </Link>
                                    </TableCell>
                                    <TableCell>
                                        {scenario.school_name || '-'}
                                    </TableCell>
                                    <TableCell>
                                        {scenario.groups &&
                                        scenario.groups.length > 1 ? (
                                            <Chip
                                                label={`${scenario.groups.length} ${translate('hallinta.tests.detail.multiple_groups')}`}
                                                size="small"
                                                variant="outlined"
                                            />
                                        ) : scenario.groups &&
                                          scenario.groups.length === 1 ? (
                                            <Link
                                                href={`#/group/${scenario.groups[0].guid}`}
                                                underline="hover"
                                            >
                                                {scenario.groups[0].name}
                                            </Link>
                                        ) : (
                                            '-'
                                        )}
                                    </TableCell>
                                    <TableCell align="center">
                                        <Chip
                                            label={scenario.teacher_count}
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        <Chip
                                            label={scenario.pupil_count}
                                            size="small"
                                            color="secondary"
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        <Chip
                                            label={scenario.student_count}
                                            size="small"
                                            color="info"
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {scenario.created
                                            ? new Date(
                                                  scenario.created
                                              ).toLocaleDateString('fi-FI')
                                            : '-'}
                                    </TableCell>
                                    <TableCell>
                                        {persistedNames.has(scenario.name) ? (
                                            <Tooltip
                                                title={translate(
                                                    'hallinta.tests.unpersist_tooltip'
                                                )}
                                            >
                                                <IconButton
                                                    size="small"
                                                    color="success"
                                                    onClick={() =>
                                                        unpersistMutation.mutate(
                                                            scenario.name
                                                        )
                                                    }
                                                    disabled={
                                                        persistMutation.isPending ||
                                                        unpersistMutation.isPending
                                                    }
                                                >
                                                    <CloudDoneIcon />
                                                </IconButton>
                                            </Tooltip>
                                        ) : (
                                            <Tooltip
                                                title={translate(
                                                    'hallinta.tests.persist_tooltip'
                                                )}
                                            >
                                                <IconButton
                                                    size="small"
                                                    onClick={() =>
                                                        persistMutation.mutate(
                                                            scenario.name
                                                        )
                                                    }
                                                    disabled={
                                                        persistMutation.isPending ||
                                                        unpersistMutation.isPending
                                                    }
                                                >
                                                    <SaveIcon />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() =>
                                                handleDelete(scenario.name)
                                            }
                                            disabled={deleteMutation.isPending}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Create Dialog with WizardForm */}
            <Dialog
                open={createDialogOpen}
                onClose={() => setCreateDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    {translate('hallinta.tests.create')}
                    <IconButton
                        onClick={() => setCreateDialogOpen(false)}
                        sx={{ position: 'absolute', right: 8, top: 8 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <WizardForm
                        onSubmit={handleCreate}
                        defaultValues={{
                            class_name: '',
                            class_code: '',
                            class_highschool: false,
                            teacher_count: 1,
                            teacher_firstname_prefix: 'Testi',
                            teacher_lastname: 'Opettaja',
                            teacher_email_domain: 'testi.fix',
                            teacher_password: 'test1=pe',
                            teacher_mpass: false,
                            pupil_count: 5,
                            pupil_firstname_prefix: 'Testi',
                            pupil_lastname: 'Oppilas',
                            pupil_keycode_prefix: 'niitty',
                            pupil_mpass: false,
                            student_count: 0,
                            student_firstname_prefix: 'Testi',
                            student_lastname: 'Opiskelija',
                            student_email_domain: 'testi.fix',
                            student_password: 'test1=pe',
                            student_mpass: false,
                            add_learners_to_groups: true,
                            assign_licenses: true,
                            accept_terms: true,
                            skip_remember_me: true,
                            class_size: 0,
                        }}
                    >
                        {/* Step 1: Basics */}
                        <WizardFormStep
                            label={translate('hallinta.tests.steps.basics')}
                        >
                            <Typography variant="subtitle1" gutterBottom>
                                {translate('hallinta.tests.steps.basics')}
                            </Typography>
                            <TextInput
                                source="name"
                                label={translate('hallinta.tests.fields.name')}
                                validate={[required(), minLength(2)]}
                                fullWidth
                            />
                            <TextInput
                                source="description"
                                label={translate(
                                    'hallinta.tests.fields.description'
                                )}
                                multiline
                                rows={3}
                                fullWidth
                            />
                        </WizardFormStep>

                        {/* Step 2: School & Materials */}
                        <WizardFormStep
                            label={translate(
                                'hallinta.tests.steps.school_materials'
                            )}
                        >
                            <Typography variant="subtitle1" gutterBottom>
                                <DomainIcon
                                    sx={{ mr: 1, verticalAlign: 'middle' }}
                                />
                                {translate('hallinta.tests.fields.school')}
                            </Typography>
                            <Box sx={{ mb: 2 }}>
                                <TextInput
                                    source="_school_search"
                                    label={translate(
                                        'hallinta.tests.fields.school'
                                    )}
                                    onChange={e =>
                                        setSchoolSearch(e.target.value)
                                    }
                                    fullWidth
                                    helperText="Hae koulua nimellä (vähintään 2 merkkiä)"
                                />
                                {selectedSchool && (
                                    <Chip
                                        icon={<DomainIcon />}
                                        label={selectedSchool.name}
                                        onDelete={() => setSelectedSchool(null)}
                                        color="primary"
                                        sx={{ mt: 1 }}
                                    />
                                )}
                                {!selectedSchool && schools.length > 0 && (
                                    <Paper
                                        sx={{
                                            maxHeight: 200,
                                            overflow: 'auto',
                                            mt: 1,
                                        }}
                                    >
                                        {schools.map(s => (
                                            <Box
                                                key={s.guid}
                                                sx={{
                                                    p: 1,
                                                    cursor: 'pointer',
                                                    '&:hover': {
                                                        bgcolor: 'action.hover',
                                                    },
                                                }}
                                                onClick={() => {
                                                    setSelectedSchool(s);
                                                    setSchoolSearch('');
                                                }}
                                            >
                                                <Typography variant="body2">
                                                    {s.name}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Paper>
                                )}
                            </Box>

                            <Divider sx={{ my: 2 }} />

                            <Typography variant="subtitle1" gutterBottom>
                                <Diversity1Icon
                                    sx={{ mr: 1, verticalAlign: 'middle' }}
                                />
                                {translate('hallinta.tests.steps.class')}
                            </Typography>
                            <TextInput
                                source="class_name"
                                label={translate(
                                    'hallinta.tests.fields.class_name'
                                )}
                                fullWidth
                                helperText={translate(
                                    'hallinta.tests.fields.class_name_help'
                                )}
                            />
                            <TextInput
                                source="class_code"
                                label={translate(
                                    'hallinta.tests.fields.class_code'
                                )}
                                fullWidth
                                helperText={translate(
                                    'hallinta.tests.fields.class_code_help'
                                )}
                            />
                            <BooleanInput
                                source="class_highschool"
                                label={translate(
                                    'hallinta.tests.fields.class_highschool'
                                )}
                            />

                            <Divider sx={{ my: 2 }} />

                            <Typography variant="subtitle1" gutterBottom>
                                <LibraryBooksIcon
                                    sx={{ mr: 1, verticalAlign: 'middle' }}
                                />
                                {translate('hallinta.tests.fields.materials')}
                            </Typography>
                            <Box sx={{ mb: 2 }}>
                                <TextInput
                                    source="_product_search"
                                    label={translate(
                                        'hallinta.tests.fields.materials'
                                    )}
                                    onChange={e =>
                                        setProductSearch(e.target.value)
                                    }
                                    fullWidth
                                    helperText="Hae materiaalia nimellä (vähintään 2 merkkiä)"
                                />
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: 0.5,
                                        mt: 1,
                                    }}
                                >
                                    {selectedProducts.map(prod => {
                                        const roleLabels = getProductRoleLabels(
                                            prod.applications
                                        );
                                        const roleSuffix =
                                            roleLabels.length > 0
                                                ? ` [${roleLabels.join(', ')}]`
                                                : '';
                                        return (
                                            <Chip
                                                key={prod.guid}
                                                label={`${prod.name}${prod.isbn ? ` (${prod.isbn})` : ''}${roleSuffix}`}
                                                onDelete={() =>
                                                    setSelectedProducts(prev =>
                                                        prev.filter(
                                                            p =>
                                                                p.guid !==
                                                                prod.guid
                                                        )
                                                    )
                                                }
                                                size="small"
                                            />
                                        );
                                    })}
                                </Box>
                                {products.length > 0 && (
                                    <Paper
                                        sx={{
                                            maxHeight: 200,
                                            overflow: 'auto',
                                            mt: 1,
                                        }}
                                    >
                                        {products
                                            .filter(
                                                p =>
                                                    !selectedProducts.find(
                                                        sel =>
                                                            sel.guid === p.guid
                                                    )
                                            )
                                            .map(p => (
                                                <Box
                                                    key={p.guid}
                                                    sx={{
                                                        p: 1,
                                                        cursor: 'pointer',
                                                        '&:hover': {
                                                            bgcolor:
                                                                'action.hover',
                                                        },
                                                    }}
                                                    onClick={() =>
                                                        setSelectedProducts(
                                                            prev => [...prev, p]
                                                        )
                                                    }
                                                >
                                                    <Typography
                                                        variant="body2"
                                                        sx={{ fontWeight: 500 }}
                                                    >
                                                        {p.name}
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                    >
                                                        {[
                                                            p.isbn,
                                                            getProductRoleLabels(
                                                                p.applications
                                                            ).join(', '),
                                                            p.target_persons?.join(
                                                                ', '
                                                            ),
                                                            p.kouluasteet?.join(
                                                                ', '
                                                            ),
                                                        ]
                                                            .filter(Boolean)
                                                            .join(' · ')}
                                                    </Typography>
                                                </Box>
                                            ))}
                                    </Paper>
                                )}
                            </Box>
                        </WizardFormStep>

                        {/* Step 3: Users */}
                        <WizardFormStep
                            label={translate('hallinta.tests.steps.users')}
                        >
                            <BooleanInput
                                source="add_learners_to_groups"
                                label={translate(
                                    'hallinta.tests.fields.add_learners_to_groups'
                                )}
                                helperText={translate(
                                    'hallinta.tests.fields.add_learners_to_groups_help'
                                )}
                            />
                            <Accordion defaultExpanded>
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                >
                                    <SchoolIcon sx={{ mr: 1 }} />
                                    <Typography>
                                        {translate(
                                            'hallinta.tests.sections.teachers'
                                        )}
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <NumberInput
                                        source="teacher_count"
                                        label={translate(
                                            'hallinta.tests.fields.count'
                                        )}
                                        validate={[minValue(0), maxValue(100)]}
                                        sx={{ mr: 2 }}
                                    />
                                    <TextInput
                                        source="teacher_firstname_prefix"
                                        label={translate(
                                            'hallinta.tests.fields.firstname_prefix'
                                        )}
                                        sx={{ mr: 2 }}
                                    />
                                    <TextInput
                                        source="teacher_lastname"
                                        label={translate(
                                            'hallinta.tests.fields.lastname'
                                        )}
                                        sx={{ mr: 2 }}
                                    />
                                    <TextInput
                                        source="teacher_email_domain"
                                        label={translate(
                                            'hallinta.tests.fields.email_domain'
                                        )}
                                        sx={{ mr: 2 }}
                                    />
                                    <TextInput
                                        source="teacher_password"
                                        label={translate(
                                            'hallinta.tests.fields.password'
                                        )}
                                    />
                                    <BooleanInput
                                        source="teacher_mpass"
                                        label={translate(
                                            'hallinta.tests.fields.mpass_profile'
                                        )}
                                        helperText={translate(
                                            'hallinta.tests.fields.mpass_profile_help'
                                        )}
                                    />
                                </AccordionDetails>
                            </Accordion>

                            <Accordion defaultExpanded>
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                >
                                    <Face5Icon sx={{ mr: 1 }} />
                                    <Typography>
                                        {translate(
                                            'hallinta.tests.sections.pupils'
                                        )}
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <NumberInput
                                        source="pupil_count"
                                        label={translate(
                                            'hallinta.tests.fields.count'
                                        )}
                                        validate={[minValue(0), maxValue(100)]}
                                        sx={{ mr: 2 }}
                                    />
                                    <TextInput
                                        source="pupil_firstname_prefix"
                                        label={translate(
                                            'hallinta.tests.fields.firstname_prefix'
                                        )}
                                        sx={{ mr: 2 }}
                                    />
                                    <TextInput
                                        source="pupil_lastname"
                                        label={translate(
                                            'hallinta.tests.fields.lastname'
                                        )}
                                        sx={{ mr: 2 }}
                                    />
                                    <TextInput
                                        source="pupil_keycode_prefix"
                                        label={translate(
                                            'hallinta.tests.fields.keycode_prefix'
                                        )}
                                    />
                                    <BooleanInput
                                        source="pupil_mpass"
                                        label={translate(
                                            'hallinta.tests.fields.mpass_profile'
                                        )}
                                        helperText={translate(
                                            'hallinta.tests.fields.mpass_profile_help'
                                        )}
                                    />
                                </AccordionDetails>
                            </Accordion>

                            <Accordion>
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                >
                                    <FaceIcon sx={{ mr: 1 }} />
                                    <Typography>
                                        {translate(
                                            'hallinta.tests.sections.students'
                                        )}
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <NumberInput
                                        source="student_count"
                                        label={translate(
                                            'hallinta.tests.fields.count'
                                        )}
                                        validate={[minValue(0), maxValue(100)]}
                                        sx={{ mr: 2 }}
                                    />
                                    <TextInput
                                        source="student_firstname_prefix"
                                        label={translate(
                                            'hallinta.tests.fields.firstname_prefix'
                                        )}
                                        sx={{ mr: 2 }}
                                    />
                                    <TextInput
                                        source="student_lastname"
                                        label={translate(
                                            'hallinta.tests.fields.lastname'
                                        )}
                                        sx={{ mr: 2 }}
                                    />
                                    <TextInput
                                        source="student_email_domain"
                                        label={translate(
                                            'hallinta.tests.fields.email_domain'
                                        )}
                                        sx={{ mr: 2 }}
                                    />
                                    <TextInput
                                        source="student_password"
                                        label={translate(
                                            'hallinta.tests.fields.password'
                                        )}
                                    />
                                    <BooleanInput
                                        source="student_mpass"
                                        label={translate(
                                            'hallinta.tests.fields.mpass_profile'
                                        )}
                                        helperText={translate(
                                            'hallinta.tests.fields.mpass_profile_help'
                                        )}
                                    />
                                </AccordionDetails>
                            </Accordion>
                        </WizardFormStep>

                        {/* Step 4: Options */}
                        <WizardFormStep
                            label={translate('hallinta.tests.steps.options')}
                        >
                            <Typography variant="subtitle1" gutterBottom>
                                <SettingsIcon
                                    sx={{ mr: 1, verticalAlign: 'middle' }}
                                />
                                {translate('hallinta.tests.steps.options')}
                            </Typography>
                            <BooleanInput
                                source="assign_licenses"
                                label={translate(
                                    'hallinta.tests.fields.assign_licenses'
                                )}
                                helperText={translate(
                                    'hallinta.tests.fields.assign_licenses_help'
                                )}
                            />
                            <BooleanInput
                                source="accept_terms"
                                label={translate(
                                    'hallinta.tests.fields.accept_terms'
                                )}
                                helperText={translate(
                                    'hallinta.tests.fields.accept_terms_help'
                                )}
                            />
                            <BooleanInput
                                source="skip_remember_me"
                                label={translate(
                                    'hallinta.tests.fields.skip_remember_me'
                                )}
                                helperText={translate(
                                    'hallinta.tests.fields.skip_remember_me_help'
                                )}
                            />
                            <NumberInput
                                source="class_size"
                                label={translate(
                                    'hallinta.tests.fields.class_size'
                                )}
                                validate={[minValue(0), maxValue(100)]}
                                helperText={translate(
                                    'hallinta.tests.fields.class_size_help'
                                )}
                                fullWidth
                            />
                            <Divider sx={{ my: 2 }} />
                            <BooleanInput
                                source="persist_to_s3"
                                label={translate(
                                    'hallinta.tests.fields.persist_to_s3'
                                )}
                                helperText={translate(
                                    'hallinta.tests.fields.persist_to_s3_help'
                                )}
                            />
                        </WizardFormStep>
                    </WizardForm>
                </DialogContent>
            </Dialog>

            {/* Detail Dialog */}
            <Dialog
                open={!!detailScenario}
                onClose={() => setDetailScenario(null)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    {detailScenario?.name}
                    <IconButton
                        onClick={() => setDetailScenario(null)}
                        sx={{ position: 'absolute', right: 8, top: 8 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    {detailScenario && (
                        <Box>
                            {/* Description */}
                            {detailScenario.description && (
                                <Alert severity="info" sx={{ mb: 2 }}>
                                    {detailScenario.description}
                                </Alert>
                            )}

                            {/* Persisted status */}
                            <Alert
                                severity={
                                    persistedNames.has(detailScenario.name)
                                        ? 'success'
                                        : 'warning'
                                }
                                icon={
                                    persistedNames.has(detailScenario.name) ? (
                                        <CloudDoneIcon />
                                    ) : (
                                        <SaveIcon />
                                    )
                                }
                                sx={{ mb: 2 }}
                            >
                                {persistedNames.has(detailScenario.name)
                                    ? translate(
                                          'hallinta.tests.detail.persisted'
                                      )
                                    : translate(
                                          'hallinta.tests.detail.not_persisted'
                                      )}
                            </Alert>

                            {/* School & Groups */}
                            <Card sx={{ mb: 2 }}>
                                <CardContent>
                                    <Typography
                                        variant="subtitle1"
                                        gutterBottom
                                    >
                                        <DomainIcon
                                            sx={{
                                                mr: 1,
                                                verticalAlign: 'middle',
                                            }}
                                        />
                                        {translate(
                                            'hallinta.tests.fields.school'
                                        )}
                                    </Typography>
                                    <Typography variant="body2" gutterBottom>
                                        {detailScenario.school_name || '-'}
                                    </Typography>

                                    <Divider sx={{ my: 1 }} />

                                    <Typography
                                        variant="subtitle1"
                                        gutterBottom
                                    >
                                        <Diversity1Icon
                                            sx={{
                                                mr: 1,
                                                verticalAlign: 'middle',
                                            }}
                                        />
                                        {translate(
                                            'hallinta.tests.steps.class'
                                        )}
                                        {detailScenario.groups &&
                                        detailScenario.groups.length > 1
                                            ? ` (${detailScenario.groups.length})`
                                            : ''}
                                    </Typography>
                                    {detailInfo.code && (
                                        <>
                                            <Typography variant="body2">
                                                {translate(
                                                    'hallinta.tests.fields.class_code'
                                                )}
                                                :{' '}
                                                <strong>
                                                    {detailInfo.code}
                                                </strong>
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                gutterBottom
                                            >
                                                {translate(
                                                    'hallinta.tests.fields.class_highschool'
                                                )}
                                                :{' '}
                                                <strong>
                                                    {detailInfo.highschool
                                                        ? 'Kyllä'
                                                        : 'Ei'}
                                                </strong>
                                            </Typography>
                                        </>
                                    )}
                                    {detailScenario.groups &&
                                        detailScenario.groups.length > 0 && (
                                            <Box sx={{ mt: 1 }}>
                                                {detailScenario.groups.map(
                                                    g => (
                                                        <Box
                                                            key={g.guid}
                                                            sx={{ mb: 0.5 }}
                                                        >
                                                            <Link
                                                                href={`#/group/${g.guid}`}
                                                                underline="hover"
                                                            >
                                                                {g.name}
                                                            </Link>
                                                        </Box>
                                                    )
                                                )}
                                            </Box>
                                        )}
                                </CardContent>
                            </Card>

                            {/* Materials */}
                            {detailInfo.materials &&
                                detailInfo.materials.length > 0 && (
                                    <Card sx={{ mb: 2 }}>
                                        <CardContent>
                                            <Typography
                                                variant="subtitle1"
                                                gutterBottom
                                            >
                                                {translate(
                                                    'hallinta.tests.fields.materials'
                                                )}{' '}
                                                ({detailInfo.materials.length})
                                            </Typography>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>
                                                            {translate(
                                                                'hallinta.tests.detail.material_name'
                                                            )}
                                                        </TableCell>
                                                        <TableCell>
                                                            {translate(
                                                                'hallinta.tests.detail.material_subject'
                                                            )}
                                                        </TableCell>
                                                        <TableCell>
                                                            {translate(
                                                                'hallinta.tests.detail.material_series'
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {detailInfo.materials.map(
                                                        (m: any, i: number) => (
                                                            <TableRow key={i}>
                                                                <TableCell>
                                                                    {m.kirja ||
                                                                        m.guid}
                                                                </TableCell>
                                                                <TableCell>
                                                                    {m.aine ||
                                                                        '-'}
                                                                </TableCell>
                                                                <TableCell>
                                                                    {m.sarja ||
                                                                        '-'}
                                                                </TableCell>
                                                            </TableRow>
                                                        )
                                                    )}
                                                </TableBody>
                                            </Table>
                                        </CardContent>
                                    </Card>
                                )}

                            {/* Users summary */}
                            <Card>
                                <CardContent>
                                    <Typography
                                        variant="subtitle1"
                                        gutterBottom
                                    >
                                        {translate(
                                            'hallinta.tests.steps.users'
                                        )}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 2 }}>
                                        <Chip
                                            icon={<SchoolIcon />}
                                            label={`${translate('hallinta.tests.sections.teachers')}: ${detailScenario.teacher_count}`}
                                            color="primary"
                                            variant="outlined"
                                        />
                                        <Chip
                                            icon={<Face5Icon />}
                                            label={`${translate('hallinta.tests.sections.pupils')}: ${detailScenario.pupil_count}`}
                                            color="secondary"
                                            variant="outlined"
                                        />
                                        <Chip
                                            icon={<FaceIcon />}
                                            label={`${translate('hallinta.tests.sections.students')}: ${detailScenario.student_count}`}
                                            color="info"
                                            variant="outlined"
                                        />
                                    </Box>
                                    <Typography variant="body2" sx={{ mt: 1 }}>
                                        {translate(
                                            'hallinta.tests.fields.created'
                                        )}
                                        :{' '}
                                        {detailScenario.created
                                            ? new Date(
                                                  detailScenario.created
                                              ).toLocaleString('fi-FI')
                                            : '-'}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Box>
                    )}
                </DialogContent>
            </Dialog>

            {/* Result Dialog */}
            <Dialog
                open={resultDialogOpen}
                onClose={() => setResultDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    {translate('hallinta.tests.result.title')}
                    <IconButton
                        onClick={() => setResultDialogOpen(false)}
                        sx={{ position: 'absolute', right: 8, top: 8 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    {lastResult && (
                        <Box>
                            <Alert severity="success" sx={{ mb: 2 }}>
                                {translate('hallinta.tests.result.group')}:{' '}
                                {lastResult.group_guid}
                            </Alert>

                            {lastResult.teachers.length > 0 && (
                                <Card sx={{ mb: 2 }}>
                                    <CardContent>
                                        <Typography
                                            variant="subtitle1"
                                            gutterBottom
                                        >
                                            {translate(
                                                'hallinta.tests.result.teachers'
                                            )}{' '}
                                            ({lastResult.teachers.length})
                                        </Typography>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>
                                                        {translate(
                                                            'hallinta.tests.result.username'
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        {translate(
                                                            'hallinta.tests.fields.firstname_prefix'
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        {translate(
                                                            'hallinta.tests.fields.lastname'
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {lastResult.teachers.map(
                                                    (t, i) => (
                                                        <TableRow key={i}>
                                                            <TableCell>
                                                                {t.username}
                                                            </TableCell>
                                                            <TableCell>
                                                                {t.firstname}
                                                            </TableCell>
                                                            <TableCell>
                                                                {t.lastname}
                                                            </TableCell>
                                                        </TableRow>
                                                    )
                                                )}
                                            </TableBody>
                                        </Table>
                                    </CardContent>
                                </Card>
                            )}

                            {lastResult.pupils.length > 0 && (
                                <Card sx={{ mb: 2 }}>
                                    <CardContent>
                                        <Typography
                                            variant="subtitle1"
                                            gutterBottom
                                        >
                                            {translate(
                                                'hallinta.tests.result.pupils'
                                            )}{' '}
                                            ({lastResult.pupils.length})
                                        </Typography>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>
                                                        {translate(
                                                            'hallinta.tests.fields.firstname_prefix'
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        {translate(
                                                            'hallinta.tests.fields.lastname'
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        {translate(
                                                            'hallinta.tests.result.keycode'
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {lastResult.pupils.map(
                                                    (p, i) => (
                                                        <TableRow key={i}>
                                                            <TableCell>
                                                                {p.firstname}
                                                            </TableCell>
                                                            <TableCell>
                                                                {p.lastname}
                                                            </TableCell>
                                                            <TableCell>
                                                                <Chip
                                                                    label={
                                                                        p.keycode
                                                                    }
                                                                    size="small"
                                                                    color="secondary"
                                                                />
                                                            </TableCell>
                                                        </TableRow>
                                                    )
                                                )}
                                            </TableBody>
                                        </Table>
                                    </CardContent>
                                </Card>
                            )}

                            {lastResult.students.length > 0 && (
                                <Card>
                                    <CardContent>
                                        <Typography
                                            variant="subtitle1"
                                            gutterBottom
                                        >
                                            {translate(
                                                'hallinta.tests.result.students'
                                            )}{' '}
                                            ({lastResult.students.length})
                                        </Typography>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>
                                                        {translate(
                                                            'hallinta.tests.result.username'
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        {translate(
                                                            'hallinta.tests.fields.firstname_prefix'
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        {translate(
                                                            'hallinta.tests.fields.lastname'
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {lastResult.students.map(
                                                    (s, i) => (
                                                        <TableRow key={i}>
                                                            <TableCell>
                                                                {s.username}
                                                            </TableCell>
                                                            <TableCell>
                                                                {s.firstname}
                                                            </TableCell>
                                                            <TableCell>
                                                                {s.lastname}
                                                            </TableCell>
                                                        </TableRow>
                                                    )
                                                )}
                                            </TableBody>
                                        </Table>
                                    </CardContent>
                                </Card>
                            )}
                        </Box>
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default TestScenarioPanel;
